import type { Platform } from "../../client/platform";
import type { FlightDesign, FlightProfile, RunSave } from "./types";
export const emptyProfile = (): FlightProfile => ({
  unlocked: ["wing", "orbit"],
  solo: { ticks: 0, bosses: 0, level: 1 },
  coop: { ticks: 0, bosses: 0, level: 1 },
  todayStars: 0,
});
export interface FlightLibrary {
  designs: FlightDesign[];
  profile: FlightProfile;
  runIds: string[];
  activeId?: string;
}
/** Serialized writes preserve ordering; the old generation survives failed writes. */
export class FlightStorage {
  library: FlightLibrary = { designs: [], profile: emptyProfile(), runIds: [] };
  private queue = Promise.resolve();
  private savedChunks = new Map<string, number>();
  constructor(
    private platform: Platform,
    public account: string,
  ) {}
  key(key: string) {
    return `flight:${this.account}:${key}`;
  }
  async load() {
    const data = await this.read("library");
    if (data) this.library = JSON.parse(data);
    return this.library;
  }
  private async read(key: string) {
    if (this.platform.durable) return this.platform.durable.read(this.key(key));
    return this.platform.get<string | null>(this.key(key), null);
  }
  private write(key: string, value: string) {
    const save = () =>
      this.platform.durable
        ? this.platform.durable.write(this.key(key), value)
        : Promise.resolve(this.platform.set(this.key(key), value));
    const result = this.queue.then(save);
    this.queue = result.catch(() => {});
    return result;
  }
  saveLibrary() {
    return this.write("library", JSON.stringify(this.library));
  }
  async loadRun(id: string): Promise<RunSave | null> {
    const value = await this.read("run:" + id);
    if (!value) return null;
    const saved = JSON.parse(value);
    if (typeof saved.chunkCount === "number") {
      saved.chunks = [];
      for (let i = 0; i < saved.chunkCount; i++) {
        const chunk = await this.read(`chunk:${id}:${i}`);
        if (!chunk) throw new Error("战斗日志不完整，请保留存档后重试");
        saved.chunks.push(JSON.parse(chunk));
      }
      this.savedChunks.set(id, saved.chunkCount);
      delete saved.chunkCount;
    }
    return saved;
  }
  saveRun(run: RunSave) {
    const id = run.manifest.id,
      chunkCount = run.chunks.length,
      from = this.savedChunks.get(id) ?? 0;
    const chunks = run.chunks
        .slice(from)
        .map((c) => ({ index: c.index, text: JSON.stringify(c) })),
      snapshot = JSON.stringify({ ...run, chunks: undefined, chunkCount });
    if (!this.library.runIds.includes(id)) this.library.runIds.push(id);
    if (run.status === "active") this.library.activeId = id;
    else if (this.library.activeId === id) this.library.activeId = undefined;
    const library = JSON.stringify(this.library),
      put = async (key: string, text: string) => {
        if (this.platform.durable)
          await this.platform.durable.write(this.key(key), text);
        else this.platform.set(this.key(key), text);
      };
    const result = this.queue.then(async () => {
      for (const c of chunks) await put(`chunk:${id}:${c.index}`, c.text);
      await put("run:" + id, snapshot);
      await put("library", library);
      this.savedChunks.set(id, chunkCount);
    });
    this.queue = result.catch(() => {});
    return result;
  }
}
